/* COASTA: real, zero-priced Shopify gift lines. Does not render or style the cart. */
(() => {
  if (window.__coastaGiftLineSync) return;
  window.__coastaGiftLineSync = true;

  const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  const url = (path) => `${root}/${path}`;
  const giftProperty = '_coasta_free_gift';
  let inFlight = null;
  let rerun = false;
  let timer;
  let bypassCheckout = false;

  function getRules() {
    const rulesNode = document.querySelector('#CartDrawer [data-coasta-free-gift-rules]');
    if (!rulesNode) return null; // The cart section has not rendered yet.
    try {
      const records = JSON.parse(rulesNode.textContent || '[]');
      if (!Array.isArray(records)) return null;
      const rules = new Map();
      for (const record of records) {
        const id = Number(record.variantId);
        const quantity = Number(record.quantity);
        if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(quantity) && quantity > 0) {
          rules.set(id, (rules.get(id) || 0) + quantity);
        }
      }
      return rules;
    } catch (error) {
      console.warn('COASTA: invalid gift variants in cart section.', error);
      return null;
    }
  }

  async function request(path, body) {
    const response = await fetch(url(path), {
      credentials: 'same-origin',
      method: body ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result || result.status >= 400) {
      throw new Error(`Shopify free gift request failed (${response.status})`);
    }
    return result;
  }

  async function syncOnce() {
    const expected = getRules();
    if (!expected) return false;
    const cart = await request('cart.js');
    const existing = new Map();
    for (const item of cart.items || []) {
      if (String(item.properties?.[giftProperty]) !== '1') continue;
      const id = Number(item.variant_id);
      if (!existing.has(id)) existing.set(id, []);
      existing.get(id).push(item);
    }

    let changed = false;
    // Remove stale gift lines, reduce excess quantity, and normalize duplicates.
    for (const [id, lines] of existing) {
      let required = expected.get(id) || 0;
      for (const item of lines) {
        const desiredForLine = Math.min(item.quantity, required);
        required -= desiredForLine;
        if (item.quantity !== desiredForLine) {
          await request('cart/change.js', { id: item.key, quantity: desiredForLine });
          changed = true;
        }
      }
    }

    const additions = [];
    for (const [id, required] of expected) {
      const current = (existing.get(id) || []).reduce((total, item) => total + item.quantity, 0);
      const missing = required - current;
      if (missing > 0) additions.push({ id, quantity: missing, properties: { [giftProperty]: '1' } });
    }
    if (additions.length) {
      // The Liquid configuration only emits AVAILABLE variants whose Shopify price is zero.
      // Never add paid accessories merely because their metaobject says "Free Gift".
      await request('cart/add.js', { items: additions });
      changed = true;
    }
    if (changed) {
      // Update the built-in header badge, then re-render the original cart drawer.
      const updatedCart = await request('cart.js');
      if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'coasta-free-gifts', cart: updatedCart
        });
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    }
    return changed;
  }

  function reconcile() {
    if (inFlight) { rerun = true; return inFlight; }
    inFlight = (async () => {
      try { await syncOnce(); }
      catch (error) { console.warn('COASTA: real free gifts could not be synced.', error); throw error; }
    })();
    return inFlight.finally(() => {
      inFlight = null;
      if (rerun) { rerun = false; schedule(); }
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => { reconcile().catch(() => {}); }, 180);
  }

  // Checkout must wait for the actual Shopify cart lines to exist.
  // This covers the ordinary cart drawer checkout; accelerated PDP express checkout is separate.
  async function beforeCheckout(event) {
    if (bypassCheckout || event.defaultPrevented) return;
    const submitter = event.type === 'click'
      ? event.target.closest('#CartDrawer button[name="checkout"], #CartDrawer input[name="checkout"], #CartDrawer a[href*="/checkout"]')
      : event.submitter;
    const checkoutForm = event.type === 'submit' ? event.target : submitter?.form;
    if (!submitter && !(event.type === 'submit' && event.target.closest('#CartDrawer') && event.submitter?.name === 'checkout')) return;
    if (!submitter && !checkoutForm) return;
    if (!getRules()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearTimeout(timer);
    try {
      // Wait for earlier changes, then reconcile against the latest rendered section rules.
      if (inFlight) await inFlight;
      await reconcile();
    } catch (error) {
      if (!window.confirm('The free gifts could not be added to your order. Continue to checkout without them?')) return;
    }
    bypassCheckout = true;
    try {
      if (submitter?.tagName === 'A') {
        window.location.assign(submitter.href);
      } else if (checkoutForm) {
        checkoutForm.requestSubmit(submitter && submitter.type === 'submit' ? submitter : undefined);
      }
    } finally {
      // Preserve bypass for synchronous form submission, reset after it can be retried.
      setTimeout(() => { bypassCheckout = false; }, 1000);
    }
  }

  function setup() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', schedule);
    document.addEventListener('shopify:section:load', schedule);
    if (window.theme?.pubsub?.subscribe && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'coasta-free-gifts') schedule();
      });
    }
    document.addEventListener('click', beforeCheckout, true);
    document.addEventListener('submit', beforeCheckout, true);
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
})();
